<template>
    <ui-modal>
        <template #header>
            <h3 class="mb-1 text-center" v-text="configTariffTitle" />
            <h5 class="mb-0 text-center" v-text="'Płatności i rozliczenia'" />
        </template>

        <page-preloader v-if="isInitLoader" min-height="200px" />

        <template v-else>
            <div
                v-text="`Dane zostaną przekazane do Przelewy24 w imieniu Comp S.A. w celu realizacji płatności`"
                class="text-sm a-infoBar a-infoBar--warning mb-3"
            />

            <div class="payment-form">
                <div class="payment-form__col">
                    <div class="payment-form__card">
                        <template v-if="shouldShowPaymentTypeSelection">
                            <h5 v-text="'Sposób płatności'" />
                            <p
                                v-text="`Wybrany sposób płatności możesz zmienić
                                w sekcji Ustawienia w zakładce System Kaucyjny`"
                                class="mb-0 text-sm text-primary"
                            />

                            <check-list
                                type="radio"
                                size="small"
                                class="mt-2"
                                :list="$options.paymentTypes"
                                :select-row="selectPaymentType"
                                :selected-rows="[{ id: selectedPaymentType, value: true }]"
                            />
                        </template>

                        <div class="payment-form__operator">
                            <h5 v-text="'Operator płatności'" class="mb-0" />
                            <img
                                :src="require(`@/assets/img/drs/przelewy24.svg`)"
                            />
                        </div>
                        <div class="payment-form__logos">
                            <h5 v-text="'Metody płatności'" class="mb-0" />
                            <div class="payment-form__logos__logos">
                                <img
                                    v-for="logo in $options.logos"
                                    :key="logo"
                                    :src="require(`@/assets/img/drs/${logo}.svg`)"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="payment-form__card cux-exclude mt-3">
                        <h5 v-text="'Adres e-mail'" />
                        <p
                            v-text="`Na podany adres e-mail wyślemy potwierdzenie płatności`"
                            class="text-sm text-primary"
                        />

                        <the-input
                            v-model="email"
                            :text="email"
                            :invalid="!!emailError"
                            :invalid-feedback="emailError"
                            @input="emailError = ''"
                        />
                    </div>
                </div>

                <div class="payment-form__col">
                    <div class="payment-form__card cux-exclude">
                        <h5 v-text="'Dane do faktury'" />
                        <p
                            v-text="`Dane, które przekazaliśmy do COMP S.A. z EuroPlatform`"
                            class="mb-4 text-sm text-primary"
                        />

                        <the-input
                            disabled
                            :text="`${user.name} ${user.surname}`"
                            label="Imię i nazwisko"
                        />

                        <the-input disabled :text="companyData.name" label="Nazwa firmy" />

                        <the-input disabled :text="companyData.nip" label="NIP" />

                        <the-input disabled :text="companyData.street" label="Ulica" />

                        <the-input
                            disabled
                            :text="`${companyData.building}${companyData.local ? ' / ' + companyData.local : ''}`"
                            label="Nr budynku / lokalu"
                        />

                        <div class="d-flex gap-1">
                            <the-input
                                disabled
                                :text="companyData.postalCode"
                                label="Kod pocztowy"
                                style="flex: 1"
                            />
                            <the-input
                                disabled
                                :text="companyData.city"
                                label="Miejscowość"
                                style="flex: 1"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <hr />

            <recap-table />
        </template>

        <template #footer>
            <the-btn
                class="btn-outline-custom"
                text="Wróć"
                :disabled="isSubmitLoading"
                @click="!isSubmitLoading && $store.dispatch('openModal', {
                    componentPath: 'drs/configuration/ConfigurationSummary'
                })"
            />
            <the-btn
                class="btn-secondary"
                :text="isVerified && !isYearlyTariff ? 'Zamawiam pakiet' : 'Płacę z Przelewy24'"
                :disabled="isSubmitLoading"
                :icon="isSubmitLoading ? 'Spinner' : ''"
                :loading-status="isSubmitLoading ? 'loading' : 'idle'"
                @click="startPayment"
            />
        </template>
    </ui-modal>
</template>

<script>
import { registerPageModule } from '@/utils/store'
import { CONFIG_MODES } from '@/store/modules/drs/tariffs.module'
import DrsOperatorsStore from '@/store/modules/drs/drs-operators.module'
import DrsStoresConfigurationStore from '@/store/modules/drs/drs-stores-configuration.module'
import { mapActions, mapGetters } from 'vuex'
import RecapTable from '@/components/drs/configuration/RecapTable.vue'
import cloneDumb from '@/helpers/cloneDumb'
import { PAYMENT_TYPES } from '@/utils/constants/drs/payment'
import { COMPANY_STATUSES } from '@/utils/constants/drs/company-statuses'
import * as Sentry from '@sentry/vue'
import { TIMELINES } from '@/utils/constants/drs/tariffs'

const LOGOS = ['visa', 'mastercard', 'blik', 'bank']

export default {
    paymentTypes: PAYMENT_TYPES,

    logos: LOGOS,

    data: () => ({
        isInitLoader: false,
        isLoader: false,
        emailError: '',
        paymentUrl: '',
        isVerified: false, // User has already paid for some services,
        isEditMode: false
    }),

    computed: {
        ...mapGetters('drsTariffs', ['selectedTariff', 'tariffs', 'configTariffTitle', 'configMode', 'isCartLoading']),
        ...mapGetters('drs', ['nip', 'companiesData']),
        ...mapGetters('payment', ['paymentInfo']),

        user() {
            return this.$store.getters.getUser
        },

        companyData() {
            return this.companiesData[0]
        },

        email: {
            get() {
                return this.$store.getters['drsTariffs/invoiceEmail']
            },
            set(value) {
                this.$store.commit('drsTariffs/SET_INVOICE_EMAIL', value)
            }
        },

        selectedPaymentType: {
            get() {
                return this.$store.getters['drsTariffs/selectedPaymentType']
            },
            set(value) {
                this.$store.commit('drsTariffs/SET_SELECTED_PAYMENT_TYPE', value.id)
            }
        },

        tariff() {
            return this.selectedTariff // Get first tariff
        },

        isYearlyTariff() {
            return this.tariff?.timeline === TIMELINES.YEARLY
        },

        savedPaymentType() {
            return this.paymentInfo?.payment_type
        },

        shouldShowPaymentTypeSelection() {
            return !this.isYearlyTariff || !this.savedPaymentType
        },

        selectedShops() {
            return this.selectedTariff.locations.filter(el => el.selected)
        },

        isPaymentRoute() {
            return this.$router.currentRoute.name === 'drs-payment'
        },

        isSubmitLoading() {
            return this.isInitLoader || this.isLoader || this.isCartLoading
        }
    },

    methods: {
        validateEmail() {
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            if (!emailPattern.test(this.email)) {
                this.emailError = 'Podaj poprawny adres e-mail.'
            } else {
                this.emailError = ''
            }
        },
        ...mapActions('drs', ['loadDataAndCheckPermission']),
        ...mapActions('drsTariffs', ['postSubscription']),
        ...mapActions('payment', ['loadUpcomingPayment', 'loadPaymentInfo']),

        selectPaymentType(type) {
            this.selectedPaymentType = type
        },

        async startPayment() {
            this.validateEmail()

            if (this.emailError || this.isSubmitLoading) {
                return
            }

            this.isLoader = true
            try {
                const { status, data } = await this.postSubscription()
                if (status !== 200) {
                    this.$store.dispatch('addNotification', {
                        type: 'danger',
                        content: 'Wystąpił błąd podczas zapisu subskrypcji. Spróbuj ponownie później.'
                    })
                    throw new Error('Subscription save failed')
                } else {
                    this.paymentUrl = data.payment_url
                }

                await this.loadDataAndCheckPermission()

                // update upcoming payment data
                if (this.isPaymentRoute) {
                    this.loadUpcomingPayment()
                }

                if (this.paymentUrl) {
                    window.location.href = this.paymentUrl
                } else {
                    this.$store.dispatch('openModal', {
                        componentPath: 'drs/configuration/ThanksModal',
                        args: {
                            shopsList: cloneDumb(this.selectedShops)
                        }
                    })

                    this.$store.commit('drsTariffs/RESET_DATA')
                }
            } catch(err) {
                console.error('Error saving configuration:', err)
            } finally {
                this.isLoader = false
            }
        },

        async getCurrentTransaction() {
            try {
                const { data } = await this.$store.$api.get('/drs/api/proxy/zor/payments/current_transaction', {
                    params: {
                        nip: this.nip,
                        service_code: 'KEC'
                    }
                })

                if (data?.state === COMPANY_STATUSES.VERIFIED) {
                    this.isVerified = true
                }
            } catch (error) {
                Sentry.withScope((scope) => {
                    scope.setLevel('error')
                    scope.setTag('module', 'drs')
                    scope.setTag('endpoint', 'drs/api/proxy/zor/payments/current_transaction')
                    scope.setTag('file', 'PaymentForm.vue')
                    scope.setContext('details', {
                        userId: this.$store.getters.userId,
                        nip: this.nip
                    })

                    Sentry.captureException(error)
                })

                console.error('Error fetching current transaction:', error)
            }
        },

        async initializePaymentType() {
            if (!this.isYearlyTariff) {
                return
            }

            await this.loadPaymentInfo()
            this.$store.commit(
                'drsTariffs/SET_SELECTED_PAYMENT_TYPE',
                this.savedPaymentType || 'manual'
            )
        },

        saveTariffConfig() {
            if (this.configMode === CONFIG_MODES.EDIT) {
                return
            }

            this.$store.$storage.setItem('tariffConfig', JSON.stringify({
                selectedTariff: this.selectedTariff,
                invoiceEmail: this.email,
                selectedPaymentType: this.selectedPaymentType,
                tariffs: this.tariffs
            }))
        },

        async loadPreviousConfiguration() {
            const tariffConfig = await this.$store.$storage.getItem('tariffConfig')
            if (tariffConfig) {
                this.$store.dispatch('openModal', {
                    componentPath: 'drs/configuration/ConfigurationSummary'
                })

                await this.loadTariffConfigFromStorage()
            }
        }
    },

    beforeCreate() {
        registerPageModule(this.$store, DrsOperatorsStore, 'drsOperators')
        registerPageModule(this.$store, DrsStoresConfigurationStore, 'drsConfiguration')
    },

    async created() {
        if (!this.email) {
            this.email = this.user.email
        }

        this.isInitLoader = true
        await Promise.all([
            this.getCurrentTransaction(),
            this.initializePaymentType()
        ])
        // await this.loadPreviousConfiguration()
        this.isInitLoader = false

        this.saveTariffConfig()
    },

    components: {
        RecapTable
    }
}
</script>

<style lang="scss">
@import '@/styles/_custom.scss';
@import '~bootstrap/scss/mixins';

.payment-form {
    display: flex;
    gap: 1rem;

    &__col {
        flex: 1;
    }

    &__card {
        border: 1px solid var(--color-gray-200);
        border-radius: 5px;
        padding: 1rem;
    }

    &__operator {
        margin-top: 1rem;
        display: flex;
        align-items: center;
        justify-content: space-between;

        img {
            width: 100px;
        }
    }

    &__logos {
        margin-top: 1rem;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        flex-wrap: wrap;

        > :nth-child(1) {
            flex: 1;
        }

        &__logos {
            display: flex;
            align-items: center;
            gap: 1rem;
            margin-left: auto;

            img {
                width: 50px;
            }
        }
    }

    @include media-breakpoint-down(md) {
        flex-direction: column;
    }
}
</style>